import { queryOptions, useQuery } from '@tanstack/react-query';

import { GET_CREDIT_ALLOWANCE, type CreditAllowanceResponse } from '../../contracts';
import { getCreditAllowance } from '../api/credit-allowance';
import { getCommerceDataConfig } from '../config';

export const CREDIT_ALLOWANCE_STALE_TIME_MS = 5 * 60 * 1000;

export type UseQueryCreditAllowanceProps = {
  /** `Boolean(useQueryUser().data)`, resolved app-side. */
  hasUser: boolean;
  enabled?: boolean;
};

export type CreditAllowanceQueryOptions = {
  enabled: boolean;
};

/** TanStack query options for the current credit allowance balance. */
export const creditAllowanceQueryOptions = ({ enabled }: CreditAllowanceQueryOptions) =>
  queryOptions({
    queryKey: [GET_CREDIT_ALLOWANCE],
    enabled,
    staleTime: CREDIT_ALLOWANCE_STALE_TIME_MS,
    queryFn: async (): Promise<CreditAllowanceResponse> => {
      const { data } = await getCreditAllowance();
      return data ?? null;
    },
  });

/**
 * GET /credit-allowance — current-cycle member credit balance (may be null).
 * Dev usage presets stay off this query so other surfaces keep the real balance.
 */
export function useQueryCreditAllowance({
  hasUser,
  enabled = true,
}: UseQueryCreditAllowanceProps) {
  const { useAuthToken } = getCommerceDataConfig();
  const token = useAuthToken();

  return useQuery({
    ...creditAllowanceQueryOptions({
      enabled: enabled && !!token && hasUser,
    }),
  });
}

/** Peek at cached allowance without subscribing. */
export const getCurrentCreditAllowance = () =>
  getCommerceDataConfig().queryClient.getQueryData<CreditAllowanceResponse>([
    GET_CREDIT_ALLOWANCE,
  ]);

export const invalidateCreditAllowance = () =>
  getCommerceDataConfig().queryClient.invalidateQueries({
    queryKey: [GET_CREDIT_ALLOWANCE],
  });
